import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { LoadingOutlined, CheckCircleFilled } from "@ant-design/icons";
import { useSearchParams } from "react-router-dom";
import { TargetLocation } from "./dtos/TargetLocation";
import { TruckingGuestRoutePointDto } from "./dtos/ShipmentTruckingGuestInfoResponseDto";
import { useShipmentTruckingGuestInfo } from "./hooks/useShipmentTruckingGuestInfo";
import { useRouteLine } from "./hooks/useRouteLine";
import { useCheckinCheckout } from "./hooks/useCheckinCheckout";
import { useTargetMarker } from "./hooks/useTargetMarker";
import { useGeofence } from "./hooks/useGeofence";
import { DriverDispatchInfo } from "./components/DriverDispatchInfo";
import { Map } from "./components/Map";
import { useAllRouteMarkers } from "./hooks/useAllRouteMarkers";
import { calculateDistance } from "./utils/calculateDistance";
import { useCommonForm } from "../../base/components/form/Common";
import { CommonMessage } from "../../base/components/message/CommonMessage";
import { CommonSpin } from "../../base/components/spin/CommonSpin";
import { useMapSetup } from "./hooks/useMapSetup";
const CHECKIN_RADIUS_KM: number = 3;

const calcInitialStep: (points: TruckingGuestRoutePointDto[]) => number = (points: TruckingGuestRoutePointDto[]): number => {
  const idx: number = points.findIndex((p) => p.status < 2);
  return idx === -1 ? points.length : idx;
};

const TripCompleteScreen: () => JSX.Element = () => (
  <div className="flex flex-col items-center justify-center h-full gap-3 p-8 text-center bg-white">
    <div className="w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center">
      <CheckCircleFilled style={{ fontSize: 28, color: "#374151" }} />
    </div>
    <h2 className="text-base font-semibold text-gray-800">HOÀN TẤT</h2>
    <p className="text-sm text-gray-400 max-w-xs">Tất cả các điểm dừng đã được check-in và check-out thành công.</p>
  </div>
);

const CheckInCheckOutContainer: () => JSX.Element = () => {
  const form = useCommonForm();
  const [searchParams] = useSearchParams();
  const [isMobile, setIsMobile] = useState<boolean>(false);
  const [targetLocation, setTargetLocation] = useState<TargetLocation | null>(null);
  const [currentStep, setCurrentStep] = useState<number>(0);
  const initialStepSetRef = useRef<boolean>(false);
  const isMountedRef = useRef<boolean>(true);
  const lastPosRef = useRef<{ lat: number; lng: number; time: number } | null>(null);

  const truckingId: string | null = searchParams.get("truckingId");
  const { data: truckingInfo, isLoading: truckingLoading } = useShipmentTruckingGuestInfo(truckingId);

  const TRIP_DONE_KEY: string = `trip_done_${truckingId}`;

  const [isTripDoneOverride, setIsTripDoneOverride]: [boolean, Dispatch<SetStateAction<boolean>>] = useState(
    () => sessionStorage.getItem(TRIP_DONE_KEY) === "true",
  );

  const sortedRoutePoints: TruckingGuestRoutePointDto[] = useMemo(
    () => [...(truckingInfo?.routePoints ?? [])].sort((a, b) => a.stopOrder - b.stopOrder),
    [truckingInfo],
  );
  const totalSteps: number = sortedRoutePoints.length;

  const { mapRef, mapInst, mapReady, coords, geoReady, geoError } = useMapSetup(isMountedRef, (msg) => CommonMessage.warning(msg));

  const currentRoutePoint: TruckingGuestRoutePointDto | null = sortedRoutePoints[currentStep] ?? null;
  const nextRoutePoint: TruckingGuestRoutePointDto | null = sortedRoutePoints[currentStep + 1] ?? null;
  const isLast: boolean = currentStep === totalSteps - 1;

  const addressSelected: (lat: number, lng: number, label: string) => void = useCallback(
    (lat: number, lng: number, label: string) => {
      setTargetLocation({ lat, lng, label });
      if (mapInst.current && mapReady) {
        try {
          mapInst.current.flyTo([lat, lng], 15, { duration: 1.2 });
        } catch {}
      }
    },
    [mapInst, mapReady],
  );

  const { distanceToTarget, isWithinGeofence } = useGeofence(coords, targetLocation, CHECKIN_RADIUS_KM);

  useRouteLine({ mapInst, mapReady, coords, targetLocation, accentColor: "#374151" });

  const reCenter: () => void = useCallback(() => {
    if (!coords || !mapInst.current || !mapReady) return;
    try {
      mapInst.current.flyTo(coords, 16, { duration: 1 });
    } catch {}
  }, [coords, mapReady, mapInst]);

  const advance: () => void = useCallback(() => {
    if (!isMountedRef.current) return;
    if (isLast) {
      CommonMessage.success("Hoàn tất chuyến đi thành công!");
      sessionStorage.setItem(TRIP_DONE_KEY, "true");
      setIsTripDoneOverride(true);
      setCurrentStep(totalSteps);
    } else {
      setCurrentStep((prev) => prev + 1);
    }
  }, [isLast, totalSteps, TRIP_DONE_KEY]);

  const { checkinState, checkinTime, eventLoading, manualCheckin, manualCheckout } = useCheckinCheckout({
    shipmentTruckingId: truckingId,
    currentRoutePoint,
    isWithinGeofence,
    geoReady,
    coords,
    isLast,
    isMountedRef,
    onAdvance: advance,
  });

  const isBootstrapping: boolean = !!truckingId && truckingLoading;

  const isTripDone: boolean =
    isTripDoneOverride ||
    !!truckingInfo?.isCompleted ||
    (currentStep >= totalSteps && totalSteps > 0) ||
    (totalSteps > 0 && sortedRoutePoints.every((p) => p.status >= 2));

  const { processGeocoding } = useTargetMarker({
    form,
    addressField: "__address__",
    coordLatField: "__lat__",
    coordLngField: "__lng__",
    stepLabel: currentRoutePoint?.stopTypeName ?? "",
    stepIcon: "",
    mapInst,
    mapReady,
    isMountedRef,
    targetLocation,
    setTargetLocation,
    setLoading: () => {},
    accentColor: "#00c04a",
    radiusKm: CHECKIN_RADIUS_KM,
  });

  useAllRouteMarkers({
    mapInst,
    mapReady,
    isMountedRef,
    routePoints: sortedRoutePoints,
    currentStep,
    accentColor: "#00c04a",
  });

  useEffect(() => {
    if (!coords) return;
    const [lat, lng]: [number, number] = coords;
    const now: number = Date.now();

    if (lastPosRef.current) {
      const dist: number = calculateDistance(lastPosRef.current.lat, lastPosRef.current.lng, lat, lng);
      const timeDiff: number = (now - lastPosRef.current.time) / 1000; // giây

      if (timeDiff > 2) {
        const speed: number = (dist / timeDiff) * 3.6; // km/h
        if (speed > 200) {
          // Nếu tốc độ > 200km/h
          CommonMessage.error("Phát hiện gian lận vị trí! Vui lòng tắt các phần mềm giả lập.");
          // Bạn có thể set một state để vô hiệu hóa nút Check-in ở đây
        }
      }
    }
    lastPosRef.current = { lat, lng, time: now };
  }, [coords]);

  useEffect(() => {
    if (!truckingInfo || initialStepSetRef.current) return;
    initialStepSetRef.current = true;
    setCurrentStep(calcInitialStep(sortedRoutePoints));
  }, [truckingInfo, sortedRoutePoints]);

  useEffect(() => {
    if (!truckingInfo) return;
    form.setFieldsValue({
      identity: truckingInfo.identity ?? "",
      driverName: truckingInfo.driverName ?? "",
      driverPhone: truckingInfo.driverPhone ?? "",
      licensePlate: truckingInfo.vehiclePlate ?? "",
      shipmentBill: truckingInfo.shipmentBill ?? "",
      containerNo: truckingInfo.containerNo ?? "",
      sealNo: truckingInfo.sealNo ?? "",
      vendorName: truckingInfo.vendorName ?? "",
    });
  }, [truckingInfo, form]);

  useEffect(
    () => () => {
      isMountedRef.current = false;
    },
    [],
  );

  useEffect(() => {
    const mq: MediaQueryList = window.matchMedia("(max-width: 768px)");
    const handler: (e: MediaQueryListEvent) => void = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    setIsMobile(mq.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  useEffect(() => {
    setTargetLocation(null);
    const point = sortedRoutePoints[currentStep];
    if (!point) return;

    if (point.targetLat != null && point.targetLng != null) {
      setTargetLocation({
        lat: point.targetLat,
        lng: point.targetLng,
        label: point.checkInAddress ?? point.stopTypeName,
      });
      if (mapInst.current && mapReady) {
        try {
          mapInst.current.flyTo([point.targetLat, point.targetLng], 15, { duration: 1.2 });
        } catch {}
      }
      return;
    }

    processGeocoding();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep, sortedRoutePoints, mapReady]);

  return (
    <>
      <style>{`
        @keyframes _gps_pulse {
          0%   { transform: scale(1); opacity: 0.7; }
          100% { transform: scale(2.4); opacity: 0; }
        }
        .leaflet-control-attribution { display: none !important; }
        .full-height-spin .ant-spin-container {
          height: 100% !important;
          display: flex;
          flex-direction: column;
        }
      `}</style>

      <div className="fixed inset-0 overflow-hidden">
        <CommonSpin
          spinning={isBootstrapping || eventLoading}
          indicator={<LoadingOutlined spin style={{ fontSize: 32, color: "#374151" }} />}
          tip={isBootstrapping ? "Đang tải thông tin chuyến xe..." : eventLoading ? "Đang ghi nhận..." : undefined}
          wrapperClassName="full-height-spin h-full"
        >
          {isTripDone ? (
            <TripCompleteScreen />
          ) : (
            <div className={`h-full w-full ${isMobile ? "flex flex-col" : "flex flex-row"}`}>
              <div className={`shrink-0 ${isMobile ? "h-[40%]" : "flex-1"}`} style={isMobile ? undefined : { minWidth: 0 }}>
                <Map mapRef={mapRef} geoReady={geoReady} geoError={geoError} coords={coords} accentColor="#374151" onRecenter={reCenter} />
              </div>

              <div className={`overflow-hidden ${isMobile ? "flex-1 min-h-0" : "shrink-0"}`} style={isMobile ? undefined : { width: "360px" }}>
                <DriverDispatchInfo
                  truckingId={truckingId as string}
                  form={form}
                  currentStep={currentStep}
                  totalSteps={totalSteps}
                  geoReady={geoReady}
                  geoError={geoError}
                  targetLocation={targetLocation}
                  distanceToTarget={distanceToTarget}
                  isWithinGeofence={isWithinGeofence}
                  loading={isBootstrapping || eventLoading}
                  isLast={isLast}
                  checkinState={checkinState}
                  checkinTime={checkinTime}
                  onAddressSelected={addressSelected}
                  currentRoutePoint={currentRoutePoint}
                  nextRoutePoint={nextRoutePoint}
                  checkinRadiusKm={CHECKIN_RADIUS_KM}
                  routePoints={sortedRoutePoints}
                  truckingFiles={truckingInfo?.files}
                  onManualCheckin={manualCheckin}
                  onManualCheckout={manualCheckout}
                />
              </div>
            </div>
          )}
        </CommonSpin>
      </div>
    </>
  );
};

export default CheckInCheckOutContainer;
